import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { signOut } from '../../lib/auth';
import { PLAN_TIERS } from '../../utils/constants';
import { createCheckoutSession, openCustomerPortal, isStripeConfigured } from '../../lib/stripe';
import './SettingsBilling.css';

export default function SettingsBilling() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const [practitioner, setPractitioner] = useState(null);
  const [clientCount, setClientCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Profile form
  const [name, setName] = useState('');
  const [practiceName, setPracticeName] = useState('');
  const [saving, setSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');

  // Billing state
  const [billingMsg, setBillingMsg] = useState('');
  const [billingLoading, setBillingLoading] = useState(false);

  useEffect(() => {
    if (user) loadSettings();
  }, [user]);

  useEffect(() => {
    // Check for Stripe redirect
    const billingStatus = searchParams.get('billing');
    if (billingStatus === 'success') {
      setBillingMsg('Subscription updated successfully!');
    } else if (billingStatus === 'cancelled') {
      setBillingMsg('Checkout was cancelled.');
    }
  }, [searchParams]);

  async function loadSettings() {
    setLoading(true);

    const { data: prac } = await supabase
      .from('practitioners')
      .select('*')
      .eq('auth_user_id', user.id)
      .single();

    if (prac) {
      setPractitioner(prac);
      setName(prac.name || '');
      setPracticeName(prac.practice_name || '');

      // Get client count
      const { count } = await supabase
        .from('clients')
        .select('id', { count: 'exact', head: true })
        .eq('practitioner_id', prac.id);

      setClientCount(count || 0);
    }

    setLoading(false);
  }

  async function handleSaveProfile(e) {
    e.preventDefault();
    setSaving(true);
    setProfileMsg('');

    const { error } = await supabase
      .from('practitioners')
      .update({ name, practice_name: practiceName })
      .eq('id', practitioner.id);

    if (error) {
      setProfileMsg('Failed to save. Please try again.');
    } else {
      setProfileMsg('Profile updated!');
      setTimeout(() => setProfileMsg(''), 3000);
    }
    setSaving(false);
  }

  async function handleSelectPlan(tierKey) {
    setBillingLoading(true);
    setBillingMsg('');

    const result = await createCheckoutSession(tierKey, practitioner.email, practitioner.id);

    if (result.error) {
      setBillingMsg(result.error);
    } else if (result.message) {
      setBillingMsg(result.message);
    }
    setBillingLoading(false);
  }

  async function handleManageBilling() {
    setBillingLoading(true);
    const result = await openCustomerPortal(practitioner.stripe_customer_id);
    if (result.error) {
      setBillingMsg(result.error);
    } else if (result.message) {
      setBillingMsg(result.message);
    }
    setBillingLoading(false);
  }

  async function handleLogout() {
    await signOut();
    navigate('/login', { replace: true });
  }

  if (loading) {
    return (
      <div className="screen container">
        <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--color-text-muted)' }}>
          Loading...
        </div>
      </div>
    );
  }

  const currentTier = practitioner?.plan_tier || 'starter';
  const currentPlan = PLAN_TIERS[currentTier];
  const stripeReady = isStripeConfigured();

  // Check if trial is active
  const trialEnds = practitioner?.trial_ends_at ? new Date(practitioner.trial_ends_at) : null;
  const isTrialActive = trialEnds && trialEnds > new Date();
  const trialDaysLeft = isTrialActive
    ? Math.ceil((trialEnds - new Date()) / (1000 * 60 * 60 * 24))
    : 0;

  return (
    <div className="screen container sb-screen">
      <h1 className="sb-title">Settings & Billing</h1>

      {/* Profile */}
      <div className="sb-section">
        <h3 className="sb-section-title">Practice Profile</h3>
        <form className="sb-form" onSubmit={handleSaveProfile}>
          <div className="form-group">
            <label>Your Name</label>
            <input
              type="text"
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Practice Name</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g. Harmony Wellness"
              value={practiceName}
              onChange={(e) => setPracticeName(e.target.value)}
            />
          </div>
          {profileMsg && (
            <p className={`sb-msg ${profileMsg.includes('Failed') ? 'sb-msg-error' : 'sb-msg-ok'}`}>
              {profileMsg}
            </p>
          )}
          <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={saving}>
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </form>
      </div>

      {/* Billing / Plan */}
      <div className="sb-section">
        <h3 className="sb-section-title">Subscription</h3>

        {/* Current plan summary */}
        <div className="sb-current-plan">
          <div className="sb-plan-badge">{currentPlan.name}</div>
          <div className="sb-plan-info">
            <p className="sb-plan-price">${currentPlan.price}<span>/month</span></p>
            <p className="sb-plan-usage">
              {clientCount} of {currentPlan.maxClients === Infinity ? '∞' : currentPlan.maxClients} clients used
            </p>
            {isTrialActive && (
              <p className="sb-trial-badge">{trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} left on free trial</p>
            )}
          </div>
          {practitioner?.stripe_customer_id && (
            <button
              className="btn btn-secondary"
              style={{ fontSize: '0.82rem', padding: '8px 14px' }}
              onClick={handleManageBilling}
              disabled={billingLoading}
            >
              Manage Billing
            </button>
          )}
        </div>

        {billingMsg && (
          <div className="sb-billing-msg">{billingMsg}</div>
        )}

        {/* Plan cards */}
        <div className="sb-plans">
          {Object.entries(PLAN_TIERS).map(([key, plan]) => {
            const isCurrent = key === currentTier;
            return (
              <div key={key} className={`sb-plan-card ${isCurrent ? 'sb-plan-current' : ''}`}>
                <div className="sb-plan-card-header">
                  <h4>{plan.name}</h4>
                  <p className="sb-plan-card-price">${plan.price}<span>/mo</span></p>
                </div>
                <ul className="sb-plan-features">
                  {plan.features.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
                {isCurrent ? (
                  <div className="sb-plan-current-label">Current Plan</div>
                ) : (
                  <button
                    className="btn btn-secondary sb-plan-select"
                    onClick={() => handleSelectPlan(key)}
                    disabled={billingLoading || !stripeReady}
                  >
                    {key > currentTier ? 'Upgrade' : 'Switch'}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {!stripeReady && (
          <p className="sb-stripe-notice">
            Stripe is not configured yet. Add your publishable key to .env to enable subscription management.
          </p>
        )}
      </div>

      {/* Account */}
      <div className="sb-section">
        <h3 className="sb-section-title">Account</h3>
        <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', marginBottom: '12px' }}>
          Signed in as {practitioner?.email}
        </p>
        <button className="sb-logout-btn" onClick={handleLogout}>
          Sign Out
        </button>
      </div>
    </div>
  );
}
